import { useEffect, useMemo, useRef, useState } from "react";
import type {
  PointerEvent as ReactPointerEvent,
  MouseEvent as ReactMouseEvent,
} from "react";
import type { RecordPoint } from "../fit/types";
import type { Units } from "../units";
import {
  displayDistance,
  displayElevation,
  distanceLabel,
  elevationLabel,
  formatDuration,
} from "../units";

export type XAxis = "distance" | "time";

interface Props {
  records: RecordPoint[];
  splits: number[];
  hoverIndex: number | null;
  selected: number | null;
  highlight: { startIdx: number; endIdx: number } | null;
  units: Units;
  xAxis: XAxis;
  describeIndex: (index: number) => string;
  onHover: (index: number | null) => void;
  onAddSplit: (index: number) => void;
  onMoveSplitAt: (pos: number, index: number) => void;
  onCommitMove: () => void;
  onRemoveSplitAt: (pos: number) => void;
  onSelect: (pos: number | null) => void;
}

// viewBox coordinate system; the SVG scales uniformly to its container width.
const VBW = 1000;
const VBH = 300;
const PLOT = { left: 46, right: 992, top: 26, bottom: 250 };

/** Nearest index in a sorted ascending array. */
function nearest(sorted: number[], value: number): number {
  let lo = 0;
  let hi = sorted.length - 1;
  if (value <= sorted[lo]) return lo;
  if (value >= sorted[hi]) return hi;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (sorted[mid] < value) lo = mid;
    else hi = mid;
  }
  return value - sorted[lo] <= sorted[hi] - value ? lo : hi;
}

function useMediaQuery(query: string): boolean {
  const [match, setMatch] = useState(
    () => typeof window !== "undefined" && window.matchMedia(query).matches,
  );
  useEffect(() => {
    const mq = window.matchMedia(query);
    const handler = () => setMatch(mq.matches);
    handler();
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [query]);
  return match;
}

export default function ElevationChart({
  records,
  splits,
  hoverIndex,
  selected,
  highlight,
  units,
  xAxis,
  describeIndex,
  onHover,
  onAddSplit,
  onMoveSplitAt,
  onCommitMove,
  onRemoveSplitAt,
  onSelect,
}: Props) {
  const svgRef = useRef<SVGSVGElement>(null);
  const dragPos = useRef<number | null>(null);
  const [dragging, setDragging] = useState<number | null>(null);

  const mobile = useMediaQuery("(max-width: 760px)");
  // Add cursor (scrubber) position, mobile only.
  const [cursorIdx, setCursorIdx] = useState(() =>
    Math.floor((records.length - 1) / 2),
  );
  useEffect(() => {
    setCursorIdx(Math.floor((records.length - 1) / 2));
  }, [records]);
  const downX = useRef<number | null>(null);
  const moved = useRef(false);
  const clampCursor = (i: number) =>
    Math.min(Math.max(i, 1), records.length - 2);

  const geo = useMemo(() => {
    const t0 = records[0].time;
    const xs: number[] = new Array(records.length);
    let lastDist = 0;
    for (let i = 0; i < records.length; i++) {
      if (xAxis === "distance") {
        if (records[i].distance !== null) lastDist = records[i].distance!;
        xs[i] = lastDist;
      } else {
        xs[i] = (records[i].time - t0) / 1000;
      }
    }
    const ys: number[] = new Array(records.length);
    let lastAlt = 0;
    let haveAlt = false;
    for (let i = 0; i < records.length; i++) {
      if (records[i].altitude !== null) {
        lastAlt = records[i].altitude!;
        haveAlt = true;
      }
      ys[i] = lastAlt;
    }
    const xMin = xs[0];
    const xMax = xs[xs.length - 1] || 1;
    let yMin = Infinity;
    let yMax = -Infinity;
    for (const y of ys) {
      if (y < yMin) yMin = y;
      if (y > yMax) yMax = y;
    }
    if (!haveAlt || yMin === yMax) {
      yMin = 0;
      yMax = 1;
    }
    const pad = (yMax - yMin) * 0.08;
    yMin -= pad;
    yMax += pad;
    return { xs, ys, xMin, xMax, yMin, yMax, haveAlt };
  }, [records, xAxis]);

  const plotX = (rawX: number) =>
    PLOT.left +
    ((rawX - geo.xMin) / (geo.xMax - geo.xMin)) * (PLOT.right - PLOT.left);
  const plotY = (alt: number) =>
    PLOT.bottom -
    ((alt - geo.yMin) / (geo.yMax - geo.yMin)) * (PLOT.bottom - PLOT.top);
  const xForIndex = (i: number) => plotX(geo.xs[i]);
  const leftPct = (i: number) =>
    Math.min(99, Math.max(1, (xForIndex(i) / VBW) * 100));

  // Area + line path (downsampled for a compact DOM string).
  const paths = useMemo(() => {
    const n = records.length;
    const step = Math.max(1, Math.ceil(n / 2000));
    let line = "";
    for (let i = 0; i < n; i += step) {
      line += `${i === 0 ? "M" : "L"}${xForIndex(i).toFixed(1)},${plotY(
        geo.ys[i],
      ).toFixed(1)} `;
    }
    line += `L${xForIndex(n - 1).toFixed(1)},${plotY(geo.ys[n - 1]).toFixed(1)}`;
    const area = `${line} L${PLOT.right.toFixed(1)},${PLOT.bottom} L${PLOT.left.toFixed(
      1,
    )},${PLOT.bottom} Z`;
    return { line, area };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geo]);

  const highlightGeo = useMemo(() => {
    if (!highlight || highlight.endIdx <= highlight.startIdx) return null;
    const { startIdx, endIdx } = highlight;
    const step = Math.max(1, Math.ceil((endIdx - startIdx) / 1200));
    let line = "";
    for (let i = startIdx; i <= endIdx; i += step) {
      line += `${i === startIdx ? "M" : "L"}${xForIndex(i).toFixed(1)},${plotY(
        geo.ys[i],
      ).toFixed(1)} `;
    }
    line += `L${xForIndex(endIdx).toFixed(1)},${plotY(geo.ys[endIdx]).toFixed(1)}`;
    return { x1: xForIndex(startIdx), x2: xForIndex(endIdx), line };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlight, geo]);

  /** Map a pointer event to the nearest record index. */
  const indexFromEvent = (e: { clientX: number }): number => {
    const svg = svgRef.current;
    if (!svg) return 0;
    const rect = svg.getBoundingClientRect();
    const vbX = ((e.clientX - rect.left) / rect.width) * VBW;
    const rawX =
      geo.xMin +
      ((vbX - PLOT.left) / (PLOT.right - PLOT.left)) * (geo.xMax - geo.xMin);
    return nearest(geo.xs, rawX);
  };

  /** The lap boundary within a finger's reach of a tap, or null. */
  const boundaryNear = (e: { clientX: number }): number | null => {
    const svg = svgRef.current;
    if (!svg || splits.length === 0) return null;
    const rect = svg.getBoundingClientRect();
    const vbX = ((e.clientX - rect.left) / rect.width) * VBW;
    const thresh = 24 * (VBW / rect.width); // ~24px on screen
    let best = -1;
    let bestD = Infinity;
    splits.forEach((s, pos) => {
      const d = Math.abs(xForIndex(s) - vbX);
      if (d < bestD) {
        bestD = d;
        best = pos;
      }
    });
    return bestD <= thresh ? best : null;
  };

  const clampBetweenNeighbours = (pos: number, index: number): number => {
    const lower = pos > 0 ? splits[pos - 1] + 1 : 1;
    const upper =
      pos < splits.length - 1 ? splits[pos + 1] - 1 : records.length - 2;
    return Math.min(Math.max(index, lower), upper);
  };

  // ---- pointer handling on the full-plot overlay ----
  const overlayDown = (e: ReactPointerEvent) => {
    if (!mobile) return;
    (e.currentTarget as Element).setPointerCapture(e.pointerId);
    downX.current = e.clientX;
    moved.current = false;
  };

  const overlayMove = (e: ReactPointerEvent) => {
    if (mobile) {
      if (downX.current === null) return;
      if (!moved.current && Math.abs(e.clientX - downX.current) > 6)
        moved.current = true;
      if (moved.current) {
        const idx = indexFromEvent(e);
        if (selected !== null)
          onMoveSplitAt(selected, clampBetweenNeighbours(selected, idx));
        else setCursorIdx(clampCursor(idx));
      }
      return;
    }
    // desktop: hover crosshair, or drag a grabbed handle
    if (dragPos.current !== null) {
      const idx = clampBetweenNeighbours(dragPos.current, indexFromEvent(e));
      onMoveSplitAt(dragPos.current, idx);
      onHover(idx);
    } else {
      onHover(indexFromEvent(e));
    }
  };

  const overlayUp = (e: ReactPointerEvent) => {
    if (mobile) {
      if (downX.current !== null && !moved.current) {
        // a tap
        const near = boundaryNear(e);
        const idx = indexFromEvent(e);
        if (selected === null) {
          if (near !== null) onSelect(near);
          else setCursorIdx(clampCursor(idx));
        } else if (near !== null && near !== selected) {
          onSelect(near);
        } else if (near === null) {
          onMoveSplitAt(selected, clampBetweenNeighbours(selected, idx));
          onCommitMove();
        }
      } else if (moved.current && selected !== null) {
        onCommitMove();
      }
      downX.current = null;
      moved.current = false;
      try {
        (e.currentTarget as Element).releasePointerCapture(e.pointerId);
      } catch {
        /* no-op */
      }
      return;
    }
    endDrag(e);
  };

  const overlayClick = (e: ReactMouseEvent) => {
    if (mobile) return;
    if (dragPos.current !== null) return;
    onAddSplit(indexFromEvent(e));
  };

  // ---- desktop handle drag ----
  const startDrag = (pos: number) => (e: ReactPointerEvent) => {
    e.stopPropagation();
    (e.target as Element).setPointerCapture(e.pointerId);
    dragPos.current = pos;
    setDragging(pos);
    onSelect(pos);
  };
  const endDrag = (e: ReactPointerEvent) => {
    if (dragPos.current !== null) {
      try {
        (e.target as Element).releasePointerCapture(e.pointerId);
      } catch {
        /* no-op */
      }
      dragPos.current = null;
      setDragging(null);
      onCommitMove();
    }
  };
  const onHandleMove = (pos: number) => (e: ReactPointerEvent) => {
    if (dragPos.current === null) return;
    e.stopPropagation();
    const idx = clampBetweenNeighbours(pos, indexFromEvent(e));
    onMoveSplitAt(pos, idx);
    onHover(idx);
  };

  // ---- mobile bottom-bar actions ----
  const nudge = (delta: number) => {
    if (selected === null) return;
    const idx = clampBetweenNeighbours(selected, splits[selected] + delta);
    if (idx === splits[selected]) return;
    onMoveSplitAt(selected, idx);
    onCommitMove();
  };

  const ticks = useMemo(() => {
    const out: { x: number; label: string }[] = [];
    const count = 6;
    for (let i = 0; i <= count; i++) {
      const raw = geo.xMin + ((geo.xMax - geo.xMin) * i) / count;
      const label =
        xAxis === "distance"
          ? displayDistance(raw, units).toFixed(
              geo.xMax - geo.xMin > 5000 ? 0 : 1,
            )
          : formatDuration(raw);
      out.push({ x: plotX(raw), label });
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geo, xAxis, units]);

  const yTicks = useMemo(() => {
    const out: { y: number; label: string }[] = [];
    const count = 4;
    for (let i = 0; i <= count; i++) {
      const alt = geo.yMin + ((geo.yMax - geo.yMin) * i) / count;
      out.push({
        y: plotY(alt),
        label: Math.round(displayElevation(alt, units)).toString(),
      });
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geo, units]);

  const hoverX = hoverIndex !== null ? xForIndex(hoverIndex) : null;
  const hoverRec = hoverIndex !== null ? records[hoverIndex] : null;

  const selValid = selected !== null && splits[selected] !== undefined;
  const activeIdx = selValid ? splits[selected!] : cursorIdx;
  const topPct = (PLOT.top / VBH) * 100;
  const heightPct = ((PLOT.bottom - PLOT.top) / VBH) * 100;

  return (
    <div className={`chart-wrap${mobile ? " m" : ""}`}>
      {mobile && geo.haveAlt && (
        <div
          className="m-readout"
          style={{
            left: `${Math.min(82, Math.max(18, (xForIndex(activeIdx) / VBW) * 100))}%`,
          }}
        >
          {describeIndex(activeIdx)}
        </div>
      )}

      <div className="chart-svg-box">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${VBW} ${VBH}`}
          className="elevation-chart"
          onPointerLeave={() => {
            if (!mobile && dragPos.current === null) onHover(null);
          }}
        >
          {/* grid + axes */}
          {yTicks.map((t, i) => (
            <g key={`y${i}`}>
              <line
                x1={PLOT.left}
                x2={PLOT.right}
                y1={t.y}
                y2={t.y}
                className="grid-line"
              />
              <text
                x={PLOT.left - 6}
                y={t.y + 3}
                className="axis-label y-axis-label"
              >
                {t.label}
              </text>
            </g>
          ))}
          {ticks.map((t, i) => (
            <text
              key={`x${i}`}
              x={t.x}
              y={VBH - 8}
              className="axis-label x-axis-label"
            >
              {t.label}
            </text>
          ))}

          {geo.haveAlt && <path d={paths.area} className="elev-area" />}
          {geo.haveAlt ? (
            <path d={paths.line} className="elev-line" />
          ) : (
            <text x={VBW / 2} y={VBH / 2} className="no-data-label">
              No elevation data in this file
            </text>
          )}

          {/* highlighted lap band + bold trace */}
          {highlightGeo && (
            <>
              <rect
                x={highlightGeo.x1}
                y={PLOT.top}
                width={Math.max(0, highlightGeo.x2 - highlightGeo.x1)}
                height={PLOT.bottom - PLOT.top}
                className="hl-band"
              />
              {geo.haveAlt && <path d={highlightGeo.line} className="hl-line" />}
            </>
          )}

          {/* hover crosshair (desktop) */}
          {hoverX !== null && (
            <line
              x1={hoverX}
              x2={hoverX}
              y1={PLOT.top}
              y2={PLOT.bottom}
              className="hover-line"
            />
          )}

          {/* pointer overlay */}
          <rect
            x={PLOT.left}
            y={PLOT.top}
            width={PLOT.right - PLOT.left}
            height={PLOT.bottom - PLOT.top}
            className="chart-overlay"
            onPointerDown={overlayDown}
            onPointerMove={overlayMove}
            onPointerUp={overlayUp}
            onClick={overlayClick}
          />

          {/* lap boundaries */}
          {splits.map((idx, pos) => {
            const x = xForIndex(idx);
            const isSel = selected === pos;
            return (
              <g key={pos} className={`split${isSel ? " selected" : ""}`}>
                <line
                  x1={x}
                  x2={x}
                  y1={PLOT.top}
                  y2={PLOT.bottom}
                  className="split-line"
                />
                {!mobile && (
                  <>
                    <g
                      className="split-handle-group"
                      onPointerDown={startDrag(pos)}
                      onPointerMove={onHandleMove(pos)}
                      onPointerUp={endDrag}
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelect(isSel ? null : pos);
                      }}
                    >
                      <circle cx={x} cy={PLOT.top} r={8} className="split-handle" />
                      <line
                        x1={x - 2.5}
                        x2={x - 2.5}
                        y1={PLOT.top - 3}
                        y2={PLOT.top + 3}
                        className="split-grip"
                      />
                      <line
                        x1={x + 2.5}
                        x2={x + 2.5}
                        y1={PLOT.top - 3}
                        y2={PLOT.top + 3}
                        className="split-grip"
                      />
                    </g>
                    <g
                      className="split-remove"
                      onPointerDown={(e) => {
                        e.stopPropagation();
                        onRemoveSplitAt(pos);
                      }}
                      transform={`translate(${x + 15}, ${PLOT.top - 15})`}
                    >
                      <circle r={8.5} className="split-remove-bg" />
                      <path
                        d="M-3.2,-3.2 L3.2,3.2 M3.2,-3.2 L-3.2,3.2"
                        className="split-remove-x"
                      />
                    </g>
                  </>
                )}
              </g>
            );
          })}

          {/* hover marker (desktop) */}
          {hoverX !== null && hoverRec && (
            <circle
              cx={hoverX}
              cy={plotY(geo.ys[hoverIndex!])}
              r={4}
              className="hover-dot"
            />
          )}
        </svg>

        {/* mobile screen-space overlays (finger-sized) */}
        {mobile && (
          <div className="mobile-layer">
            {splits.map((idx, pos) => (
              <div
                key={pos}
                className={`m-knob${selected === pos ? " sel" : ""}`}
                style={{ left: `${leftPct(idx)}%`, top: `${topPct}%` }}
              />
            ))}
            {!selValid && geo.haveAlt && (
              <>
                <div
                  className="m-cursor-line"
                  style={{
                    left: `${leftPct(cursorIdx)}%`,
                    top: `${topPct}%`,
                    height: `${heightPct}%`,
                  }}
                />
                <div
                  className="m-cursor-knob"
                  style={{ left: `${leftPct(cursorIdx)}%`, top: `${topPct}%` }}
                />
              </>
            )}
          </div>
        )}
      </div>

      {/* desktop drag readout tooltip */}
      {!mobile && dragging !== null && splits[dragging] !== undefined && (
        <div
          className="drag-tip"
          style={{ left: `${leftPct(splits[dragging])}%` }}
        >
          {describeIndex(splits[dragging])}
        </div>
      )}

      <div className="chart-caption">
        <span>
          Elevation ({elevationLabel(units)}) vs{" "}
          {xAxis === "distance" ? `distance (${distanceLabel(units)})` : "time"}
        </span>
        {hoverRec && (
          <span className="chart-readout">
            {formatDuration((hoverRec.time - records[0].time) / 1000)} ·{" "}
            {hoverRec.altitude !== null
              ? `${Math.round(displayElevation(hoverRec.altitude, units))} ${elevationLabel(units)}`
              : "—"}
            {hoverRec.power !== null ? ` · ${Math.round(hoverRec.power)} W` : ""}
            {hoverRec.heartRate !== null
              ? ` · ${Math.round(hoverRec.heartRate)} bpm`
              : ""}
          </span>
        )}
      </div>

      {/* mobile editing bar */}
      {mobile && (
        <div className="m-editbar">
          {!selValid ? (
            <button
              className="m-btn primary"
              onClick={() => onAddSplit(cursorIdx)}
            >
              + Add lap here
            </button>
          ) : (
            <>
              <button
                className="m-btn icon"
                onClick={() => nudge(-1)}
                aria-label="Nudge boundary earlier"
              >
                ◀
              </button>
              <button
                className="m-btn icon"
                onClick={() => nudge(1)}
                aria-label="Nudge boundary later"
              >
                ▶
              </button>
              <button
                className="m-btn danger grow"
                onClick={() => onRemoveSplitAt(selected!)}
              >
                Remove
              </button>
              <button className="m-btn grow" onClick={() => onSelect(null)}>
                Done
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
